import {Component, OnDestroy, OnInit} from '@angular/core';
import {ICart, IProductLine} from "../cart-models";
import {BaseComponent} from "../../base.component";
import {UserService} from "../../common/user.service";
import {LoggerService} from "../../common/logger.service";
import {Util} from "../../util/util";
import {CartService} from "../cart.service";
import {AlertUtilService} from "../../alert/alert-util.service";
import {Subscription} from "rxjs";
import {CartSavedProductService} from "../cart-saved-product/cart-saved-product.service";
import {AoUpsellService} from "../../ao-cart/ao-upsell/ao-upsell.service";
import {CartUtil} from "../cart-util";
import * as _ from "lodash";
import {AoUpsellUtil} from "../../ao-cart/ao-upsell/ao-upsell-util";
import {map, switchMap} from "rxjs/operators";
import {IAutoOrderUpsell} from "../../autoorder/auto-order-models";

@Component({
    selector: 'cart-product-summary',
    templateUrl: './cart-product-summary.component.html',
    styleUrls: ['../cart-product-summary-saved-product-shared.scss', './cart-product-summary.component.scss'],
    standalone: false
})
export class CartProductSummaryComponent extends BaseComponent implements OnInit, OnDestroy {

    oldVal: number;
    products: IProductLine[];
    busyProductId: number;
    subscription: Subscription;
    isAoUpsell: boolean = false;

    constructor(loggerService: LoggerService,
                userService: UserService,
                private cartService: CartService,
                private alertUtilService: AlertUtilService,
                private savedProductService: CartSavedProductService,
                private aoUpsellService: AoUpsellService
    ) {
        super(userService, loggerService);
    }

    ngOnInit() {
        this.subscription = this.cartService.cart$.subscribe((cart: ICart) => this.products = cart.products);
        this.subscription.add(this.aoUpsellService.aoUpsellLoadedSubject.subscribe((aoUpsellLoaded: boolean) => {
            const aoUpsell: boolean = aoUpsellLoaded && this.aoUpsellService.aoUpsellSubject.isPresent();
            if (aoUpsell != this.isAoUpsell) {
                this.isAoUpsell = aoUpsell;
                this.setAoUpsell(this.aoUpsellService.aoUpsellSubject.get());
            }
        }));
        this.subscription.add(this.aoUpsellService.aoUpsellSubject.watch().pipe(
            switchMap((autoOrderUpsell: IAutoOrderUpsell) => {
                return this.cartService.cart$.pipe(map((cart: ICart) => {
                    this.products = cart.products;
                    this.setAoUpsell(autoOrderUpsell);
                    return cart;
                }));
            })
        ).subscribe());
    }

    ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    increment(productLine: IProductLine) {
        if (Util.isValidQuantity(productLine.quantity)) {
            this.adjustCartQuantity(productLine,1);
        }
    }

    decrement(productLine: IProductLine) {
        if (Util.isValidQuantity(productLine.quantity) && productLine.quantity > 1) {
            this.adjustCartQuantity(productLine, -1);
        }
    }

    onChange(productLine: IProductLine) {
        this.log.debug("onChange oldValue:"+ this.oldVal, productLine);
        if (Util.isValidTempQuantity(productLine.quantity) || this.oldVal == productLine.quantity) {
            return;
        }

        if (Util.isValidQuantity(productLine.quantity)) {
            this.adjustCartQuantity(productLine, 0);
        } else {
            this.log.warn("Invalid quantity entered, reverting to old value");
            productLine.quantity = this.oldVal;
        }
    }

    onKeyup(productLine: IProductLine) {
        if(Util.isValidQuantity(productLine.quantity)) {
            this.onChange(productLine);
        }
    }

    onRemove(productLine: IProductLine ) {
        this.adjustCartQuantity(productLine, -productLine.quantity);
    }

    onFocus(productLine: IProductLine) {
        this.oldVal = productLine.quantity;
    }

    onBlur(productLine: IProductLine) {
        if (Util.isValidTempQuantity(productLine.quantity)) {
            //if they leave the field with a 0 or "", then we send it through, removing it and update totals
            productLine.quantity = 0;
            this.log.debug("Invalid quantity left on blur, removing");
            this.adjustCartQuantity(productLine, 0);
        }
    }

    public adjustCartQuantity(productLine: IProductLine, quantity: number) {
        if (!this.isProductBusy(productLine.product.id) && productLine.product.editable &&
            (productLine.product.removable || (productLine.quantity + quantity) > 0)) {
            this.busyProductId = productLine.product.id;
            this.cartService.adjustCartQuantity(productLine.product, quantity, productLine.mhpId).subscribe((success: boolean) => {
                this.busyProductId = -1;
                if (!success) {
                    this.adjustCartFailure();
                } else {
                    this.log.debug("Old value updated "+this.oldVal+" to "+productLine.quantity);
                    this.oldVal = productLine.quantity;
                }
            }, (error: any) => {
                this.busyProductId = -1;
                this.log.error("Error adjusting quantity", error);
                this.adjustCartFailure();
            });
        }
    }

    private adjustCartFailure() {
        this.alertUtilService.addFail({body: "cart.error.update.quantity", translate: true});
    }

    private isProductBusy(productId: number): boolean {
        return !!this.busyProductId && this.busyProductId == productId;
    }

    productSaveForLater(productLine: IProductLine) {
        //defaulting the quantity to 1 on save for later at this time. Quantity will not be shown, when added back to cart it will be 1.
        if (!!productLine && this.allowCartSaveForLater) {
            this.savedProductService.saveProductForLater(productLine.product.usanaId, productLine.mhpId, 1).subscribe(() => {
                this.adjustCartQuantity(productLine, -productLine.quantity);
            }, (error: any) => {
                this.busyProductId = -1;
                this.log.error("Error saving product for later", error);
                this.cartSaveForLaterFailure();
            });
        }
    }

    cartSaveForLaterFailure() {
        this.alertUtilService.addFail({body: "cart.error.save.for.later.error", translate: true});
    }

    setAoUpsell(autoOrderUpsell: IAutoOrderUpsell) {
        if (this.isAoUpsell && !!autoOrderUpsell) {
            this.aoUpsellService.currentAoUpsellProducts.set(_.filter(this.products, (cartProduct: IProductLine) => !!AoUpsellUtil.getProductInputByLine(cartProduct, autoOrderUpsell.products)));
        }
    }

    aoUpsellContains(cartProduct: IProductLine): boolean {
        return AoUpsellUtil.aoUpsellContains(cartProduct, this.aoUpsellService.currentAoUpsellProducts.get());
    }

    addAoUpsell(productLine: IProductLine) {
        this.aoUpsellService.addProduct(productLine);
    }

    removeAoUpsell(productLine: IProductLine) {
        this.aoUpsellService.removeProduct(productLine);
    }

    canAddToAoUpsell(productLine: IProductLine): boolean {
        return CartUtil.showAddToAoButtonProduct(productLine?.product);
    }
}
